/*
 * @ts-nocheck
 * Preventing TS checks with files presented in the video for a better presentation.
 */
import { useState } from 'react';
import { MODEL_REGEX, PROVIDER_REGEX, RESUME_BUILD_MARKER, RESUME_BUILD_REGEX } from '~/utils/constants';
import { Markdown } from './Markdown';
import { classNames } from '~/utils/classNames';
import type {
  TextUIPart,
  ReasoningUIPart,
  ToolUIPart as ToolInvocationUIPart,
  SourceUrlUIPart as SourceUIPart,
  FileUIPart,
  StepStartUIPart,
} from 'ai';

interface UserMessageProps {
  content: string | Array<{ type: string; text?: string; image?: string }>;
  parts:
    | (TextUIPart | ReasoningUIPart | ToolInvocationUIPart | SourceUIPart | FileUIPart | StepStartUIPart)[]
    | undefined;
}

export function UserMessage({ content, parts }: UserMessageProps) {
  const [copied, setCopied] = useState(false);

  // Extract images from parts - look for file parts with image mime types
  const images =
    parts?.filter(
      (part): part is FileUIPart =>
        part.type === 'file' &&
        'mediaType' in part &&
        typeof (part as any).mediaType === 'string' &&
        (part as any).mediaType.startsWith('image/'),
    ) || [];

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const renderImages = () => {
    if (images.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-wrap gap-2 mb-3">
        {images.map((item, index) => (
          <div key={index} className="relative flex rounded-lg border border-hairline overflow-hidden">
            <div className="h-20 w-20 bg-transparent outline-none">
              <img
                src={(item as any).url}
                alt={`Image ${index + 1}`}
                className="h-full w-full rounded-lg"
                style={{ objectFit: 'cover' }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const rawSource = Array.isArray(content) ? content.find((item) => item.type === 'text')?.text || '' : content;

  // A resume/continue message carries the full instructions for the agent but is
  // shown as a compact lime "Continue" chip — the bare text stays hidden.
  if (rawSource.includes(RESUME_BUILD_MARKER)) {
    return (
      <div className="flex w-full flex-col items-end">
        {/* Same bubble treatment as a normal sent message (hairline border,
            radius, padding, size per DESIGN.md); lime text + AI icon signal it's
            a Continue/resume action. */}
        <div className="inline-flex items-center gap-1.5 self-end rounded-[14px] rounded-br-[2px] border border-hairline bg-accent/[0.12] px-3.5 py-2.5 text-[13px] font-medium leading-[1.55] text-accent">
          <div className="i-ph:sparkle-fill text-[13px]"></div>
          Continue
        </div>
      </div>
    );
  }

  const brochure = extractBrochure(rawSource);
  // Content PDFs each render a chip. The brochure carries its own [ATTACHED_PDF]
  // block too (source="brochure"), but that's excluded — the brochure card already
  // represents it.
  const pdfDocs = extractPdfDocs(rawSource);
  const rawText = stripMetadata(rawSource);

  const renderBrochure = () => {
    if (!brochure) {
      return null;
    }

    const thumb = brochure.pageUrls[0];
    const pageCount = brochure.pageUrls.length;

    return (
      <a
        href={brochure.pdfUrl || thumb || '#'}
        target="_blank"
        rel="noopener noreferrer"
        className="mb-3 flex w-max max-w-full items-center gap-2.5 rounded-lg border border-hairline bg-raised px-2.5 py-2 no-underline transition-colors hover:border-subtle"
      >
        {thumb ? (
          <img
            src={thumb}
            alt="PDF preview"
            className="h-14 w-11 flex-shrink-0 rounded border border-hairline bg-elevated object-cover object-top"
          />
        ) : (
          <span className="i-ph:file-pdf flex-shrink-0 text-[22px] text-danger" />
        )}
        <span className="flex flex-col pr-1">
          <span className="text-[12px] font-medium text-primary">PDF document</span>
          <span className="text-[11px] text-muted">
            {pageCount > 0 ? `${pageCount} page${pageCount === 1 ? '' : 's'}` : 'Attached'}
          </span>
        </span>
      </a>
    );
  };

  const renderPdfDocs = () => {
    if (pdfDocs.length === 0) {
      return null;
    }

    return (
      <div className="mb-3 flex flex-col gap-2">
        {pdfDocs.map((doc, index) => (
          <div
            key={`${doc.name}-${index}`}
            className="flex w-max max-w-full items-center gap-2.5 rounded-lg border border-hairline bg-raised px-2.5 py-2"
          >
            <span className="i-ph:file-text flex-shrink-0 text-[22px] text-muted" />
            <span className="flex min-w-0 flex-col pr-1">
              <span className="truncate text-[12px] font-medium text-primary" title={doc.name || undefined}>
                {doc.name || 'PDF document'}
              </span>
              <span className="text-[11px] text-muted">
                {doc.pages ? `${doc.pages} page${doc.pages === 1 ? '' : 's'} · text attached` : 'Text attached'}
              </span>
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col items-end w-full group/msg">
      <div className="flex min-w-0 flex-col items-end max-w-[85%] sm:max-w-[75%]">
        {/* Hover actions (always visible on touch) */}
        <div className="flex items-center gap-1 mb-1 opacity-0 group-hover/msg:opacity-100 touch:opacity-100 transition-opacity">
          <button
            onClick={() => handleCopy(rawText)}
            aria-label="Copy message"
            className="flex items-center justify-center w-6 h-6 rounded-md bg-transparent border-0 cursor-pointer text-muted hover:text-primary hover:bg-white/[0.04] transition-colors touch:h-9 touch:w-9"
          >
            <span className={classNames(copied ? 'i-ph:check' : 'i-ph:copy', 'text-xs')} />
          </button>
        </div>
        {/* Message bubble — bottom-right corner tightened per design */}
        <div className="self-end max-w-full min-w-0 bg-sunken border border-hairline px-3.5 py-2.5 rounded-[14px] rounded-br-[2px] text-primary text-[13px] leading-[1.55] [overflow-wrap:anywhere]">
          {renderBrochure()}
          {renderPdfDocs()}
          {renderImages()}
          <Markdown html>{rawText}</Markdown>
        </div>
      </div>
    </div>
  );
}

// Pull the uploaded brochure/PDF back out of the hidden markers so the message can
// show it (the model already reads it via these URLs; the UI just wasn't rendering it).
function extractBrochure(text: string): { pdfUrl?: string; pageUrls: string[] } | null {
  const pdf = text.match(/\[BROCHURE_PDF:\s*([^\]]+)\]/);
  const pages = text.match(/\[BROCHURE_PAGES:\s*([^\]]+)\]/);

  if (!pdf && !pages) {
    return null;
  }

  return {
    pdfUrl: pdf?.[1]?.trim(),
    pageUrls: pages?.[1]
      ? pages[1]
          .split(',')
          .map((u) => u.trim())
          .filter(Boolean)
      : [],
  };
}

// Pull plain PDF attachments (read as text) back out of their hidden markers so the
// message can show compact chips instead of the raw extracted text. The brochure's
// own text block (source="brochure") is excluded — the brochure card represents it.
function extractPdfDocs(text: string): { name: string; pages: number }[] {
  const docs: { name: string; pages: number }[] = [];
  const re = /\[ATTACHED_PDF\b([^\]]*)\]/g;
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    const attrs = match[1];

    if (/source="brochure"/.test(attrs)) {
      continue;
    }

    const name = attrs.match(/name="([^"]*)"/)?.[1] ?? '';
    const pages = Number(attrs.match(/pages="(\d+)"/)?.[1] ?? 0);
    docs.push({ name, pages });
  }

  return docs;
}

function stripMetadata(content: string) {
  return content
    .replace(MODEL_REGEX, '')
    .replace(PROVIDER_REGEX, '')
    .replace(RESUME_BUILD_REGEX, '')
    .replace(/\[SELECTED_ELEMENT:[^\]]*\]\s*/g, '')
    .replace(/\[APP_ASSETS:[^\]]*\]\s*/g, '')
    .replace(/\[BROCHURE_PDF:[^\]]*\]\s*/g, '')
    .replace(/\[BROCHURE_PAGES:[^\]]*\]\s*/g, '')
    .replace(/\[BROCHURE_PAGES_HIRES:[^\]]*\]\s*/g, '')
    .replace(/\[BROCHURE_ASSETS:[^\]]*\]\s*/g, '')
    .replace(/\[ATTACHED_PDF\b[^\]]*\][\s\S]*?\[\/ATTACHED_PDF\]\s*/g, '');
}
